import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash, FaArrowLeft, FaExclamationTriangle } from "react-icons/fa";
import { apiFetch, DADMIN_BASE } from "./utils/api";
import { useAuth } from "./context/AuthContext";
import { dnAlert } from "./utils/dNews_Alert";
import useAutofillSync from "./utils/useAutofillSync";
import DOLLUZ_CORP_reversed from "./assets/img/DOLLUZ_CORP_reversed.png";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Login.css";

// Which app's banners this sign-in screen shows. Managed in dAdmin → Inside D →
// Login Page Config. The space and case MUST match dAdmin's key exactly.
const APP_KEY = "Inside D";

// Painted before the fetch resolves, and kept if the fetch fails. The sign-in screen
// has to render with the banner API (or the whole database) down — otherwise nobody can
// sign in to find out why it is down. These are exactly what dAdmin holds today.
const FALLBACK_BANNERS = [
    {
        banner_id: -1,
        tag_label: "ONE PLACE",
        headline: "Everything Dolluz, in one place",
        subline: "Announcements, policies, blogs and every app you use, behind a single sign-in.",
        gradient_from: "#0D1B2A",
        gradient_to: "#2A3D14",
    },
    {
        banner_id: -2,
        tag_label: "UPDATES",
        headline: "Nothing gets lost in a thread",
        subline: "Company updates are published once and stay findable, instead of scrolling back through chat.",
        gradient_from: "#0D1B2A",
        gradient_to: "#2A3D14",
    },
    {
        banner_id: -3,
        tag_label: "ACCESS",
        headline: "The right doors, already open",
        subline: "Your tiles are decided by your role, so you only ever see what you are meant to.",
        gradient_from: "#0D1B2A",
        gradient_to: "#2A3D14",
    },
];

// Figures for the panel stats row. The screen is UNAUTHENTICATED, so these are static
// product facts about Inside D — never a live count of employees, updates or people:
//   • 7  apps in the suite       — dAdmin, dForms, dAssist, dBug, dTime, dSlip, dEpr
//   • 1  sign-in for all         — the single Dolluz Corp session unlocks every app
//   • 3  audited standards       — HIPAA, ISO 9001:2015, ISO 27001:2013 (shown in the footer)
// Configured in dAdmin (Inside D's row is null there today), so this fallback governs.
const FALLBACK_STATS = [
    { value: 7, label: "apps in the suite" },
    { value: 1, label: "sign-in for all" },
    { value: 3, label: "audited standards" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared password field, so the eye toggle, caps hint and styling can never drift
// between the places a password is typed on this page.
//
// Declared at MODULE scope on purpose. Defining a component inside Login() creates a
// brand-new component type on every render, so React unmounts and remounts the <input>
// each keystroke and the field loses focus after one character — invisible in review and
// immediately fatal in use.
export function PasswordField({
    label, value, onChange, show, setShow, inputRef, autoComplete,
    capsHint, capsOn, onCapsKey, onCapsBlur,
}) {
    return (
        <div className="lg-field">
            <label>{label}</label>
            <div className="lg-passwrap">
                <input
                    ref={inputRef}
                    type={show ? "text" : "password"}
                    className="lg-input"
                    value={value}
                    autoComplete={autoComplete}
                    onChange={onChange}
                    onKeyUp={capsHint ? onCapsKey : undefined}
                    onBlur={capsHint ? onCapsBlur : undefined}
                />
                <button
                    type="button"
                    className="lg-passtoggle"
                    onClick={() => setShow(!show)}
                    tabIndex={-1}
                    aria-pressed={show}
                    aria-label={show ? "Hide password" : "Show password"}
                >
                    {show ? <FaEyeSlash /> : <FaEye />}
                </button>
            </div>
            {capsHint && capsOn && (
                <div className="lg-caps"><FaExclamationTriangle /> Caps Lock is on</div>
            )}
        </div>
    );
}

function Login() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [oldPass, setOldPass] = useState("");
    const [email, setEmail] = useState("");
    const [newPass, setNewPass] = useState("");
    const [confirmPass, setConfirmPass] = useState("");
    const [otpResetSource, setOtpResetSource] = useState(null);
    const [otp, setOtp] = useState("");
    const [timer, setTimer] = useState(0);
    const [otpSent, setOtpSent] = useState(false);
    const [mode, setMode] = useState("login");
    const navigate = useNavigate();
    const location = useLocation();
    const { refresh } = useAuth();
    const [otpLoading, setOtpLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showOldPass, setShowOldPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [capsOn, setCapsOn] = useState(false);

    // Signing in returns the person to the page that sent them here — logging in is not a
    // doorway into a separate area, it unlocks more on the page they were reading.
    const returnTo = location.state?.from?.pathname || "/";

    // ── Two-step sign-in ────────────────────────────────────────────────
    const [twoFactor, setTwoFactor] = useState(null); // { challenge, email, trustDays }
    const [loginOtp, setLoginOtp] = useState("");
    const [otpLeft, setOtpLeft] = useState(0);        // seconds on the 2-minute code
    const [remember, setRemember] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    // 2FA on by default so a failed banner fetch never hides the control and silently
    // implies verification is off.
    const [signinCfg, setSigninCfg] = useState({ two_factor_enabled: 1, trust_days: 14 });
    const [panelStats, setPanelStats] = useState(FALLBACK_STATS);

    // ── Banner carousel ──────────────────────────────────────────────────
    const [panels, setPanels] = useState(FALLBACK_BANNERS);
    const [slide, setSlide] = useState(0);
    const [rotateSecs, setRotateSecs] = useState(3);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        let live = true;
        // Plain fetch to dAdmin, NOT apiFetch: apiFetch prefixes Inside D's own backend
        // and sends the session cookie to another origin. This is an unauthenticated,
        // credential-less read that always answers 200; on any failure the fallbacks that
        // are already on screen simply stay.
        fetch(`${DADMIN_BASE}/api/login-banners/public?app=${encodeURIComponent(APP_KEY)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!live || !data) return;
                // Only replace on a non-empty result — no banners keeps the fallback.
                if (Array.isArray(data.banners) && data.banners.length) {
                    setPanels(data.banners);
                    setSlide(0);
                }
                if (data.rotate_seconds) setRotateSecs(data.rotate_seconds);
                // An empty array is a real answer — "show no figures" — so only
                // null/undefined falls back.
                if (Array.isArray(data.panel_stats)) setPanelStats(data.panel_stats);
                setSigninCfg({
                    two_factor_enabled: data.two_factor_enabled === undefined ? 1 : Number(data.two_factor_enabled),
                    trust_days: data.trust_days || 14,
                });
            })
            .catch(() => { /* the fallbacks are already on screen */ });
        return () => { live = false; };
    }, []);

    // Rotate, paused on hover, and never with a single banner.
    useEffect(() => {
        if (paused || panels.length < 2) return;
        const t = setInterval(
            () => setSlide((x) => (x + 1) % panels.length),
            Math.max(2, rotateSecs) * 1000
        );
        return () => clearInterval(t);
    }, [paused, panels.length, rotateSecs]);

    const active = panels[slide] || panels[0];

    // A banner is EITHER an image or a gradient + copy. An image fills the whole panel,
    // so nothing is painted over it. Served by dAdmin.
    const bannerImg = active?.image_path ? `${DADMIN_BASE}${active.image_path}` : null;

    // ── Autofill: keep React state in step with saved credentials ────────
    const userRef = useRef(null);
    const passRef = useRef(null);
    useAutofillSync([
        { ref: userRef, value: username, set: setUsername },
        { ref: passRef, value: password, set: setPassword },
    ]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has("changePassword")) setMode("changePassword");
        // apiFetch redirects here with ?revoked=1 when an admin has ended this session.
        // A bare sign-in form with no explanation would read as a bug.
        if (params.has("revoked")) {
            dnAlert.warning(
                "Signed out",
                "An administrator ended your session. Sign in again to continue."
            );
        }
    }, []);

    const backToLogin = useCallback(() => {
        setMode("login");
        navigate("/login");
        setOtpSent(false);
        setTimer(0);
        setOtp("");
        setOtpResetSource(null);
    }, [navigate]);

    const onPasswordKey = (e) => {
        if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
    };

    // Signed in — pick up the new session and return them where they were.
    const finishSignIn = async (successBody) => {
        dnAlert.success("Login Successful!", successBody);
        await refresh();
        navigate(returnTo, { replace: true });
    };

    const handleLogin = async () => {
        if (!username || !password) {
            return dnAlert.warning("Validation Error", "Username and password are required");
        }
        if (!EMAIL_RE.test(username)) {
            return dnAlert.error("Invalid Email", "Please enter a valid email address.");
        }

        setLoading(true);
        try {
            const res = await apiFetch("/api/login/Verifylogin", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
                credentials: "include",
            });

            const data = await res.json();
            if (!res.ok) {
                if (data.message === "EMAIL_NOT_FOUND") {
                    return dnAlert.warning(
                        "Email Not Registered",
                        "This email is not registered with Dolluz Corp. Please contact admin@dolluzcorp.com."
                    );
                }
                if (data.message === "INVALID_CREDENTIALS") {
                    return dnAlert.error(
                        "Invalid Credentials",
                        "The password you entered is incorrect. Please try again."
                    );
                }
                throw new Error(data.message || "Login failed");
            }

            // 2FA on and not inside the trust window: only a challenge was issued, not the
            // cookie. Move to the code step.
            if (data.twoFactor) {
                setTwoFactor({
                    challenge: data.challenge,
                    email: data.email,
                    trustDays: data.trustDays || signinCfg.trust_days,
                });
                setLoginOtp("");
                setOtpLeft(data.expiresInSeconds || 120);
                setMode("loginOtp");
                dnAlert.info("Check your email", `We sent a sign-in code to ${data.email}.`);
                return;
            }

            await finishSignIn("Redirecting…");
        } catch (err) {
            console.error("Login error caught in frontend:", err);
            dnAlert.error("Login Failed", err.message);
        } finally {
            setLoading(false);
        }
    };

    // Finish the two-step sign-in — this is what actually gets the user a session.
    const handleVerifyLoginOtp = async () => {
        if (!twoFactor?.challenge) return;
        if (!loginOtp || loginOtp.length < 6) {
            return dnAlert.warning("Enter the 6-digit code", "Check the email we just sent.");
        }
        setVerifying(true);
        try {
            const res = await apiFetch("/api/login/verify-login-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    challenge: twoFactor.challenge,
                    otp: loginOtp,
                    remember: signinCfg.two_factor_enabled ? remember : false,
                }),
                credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not verify the code.");

            setTwoFactor(null);
            setOtpLeft(0);
            await finishSignIn(
                data.remembered
                    ? `You will not be asked again for ${twoFactor.trustDays} days.`
                    : "Redirecting…"
            );
        } catch (err) {
            dnAlert.error("Verification failed", err.message);
        } finally {
            setVerifying(false);
        }
    };

    const handleResendLoginOtp = async () => {
        if (!twoFactor?.challenge || resending) return;
        setResending(true);
        try {
            const res = await apiFetch("/api/login/resend-login-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ challenge: twoFactor.challenge }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not resend the code.");
            setLoginOtp("");
            setOtpLeft(data.expiresInSeconds || 120);
            dnAlert.success("Code resent", `A new code is on its way to ${twoFactor.email}.`);
        } catch (err) {
            dnAlert.error("Could not resend", err.message);
        } finally {
            setResending(false);
        }
    };

    // Abandoning the code step must clear the challenge, or a stale one could be replayed.
    const cancelTwoFactor = () => {
        setTwoFactor(null);
        setLoginOtp("");
        setOtpLeft(0);
        setPassword("");
        setMode("login");
    };

    const handleVerifyOldPassword = async () => {
        if (!oldPass) return dnAlert.info("Please enter your old password");

        try {
            const res = await apiFetch("/api/login/verify-old-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ oldPass }),
                credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            dnAlert.success("Password Verified!");
            setMode("setNewPassword");
        } catch (err) {
            dnAlert.error(err.message);
        }
    };

    const handleUpdatePassword = async () => {
        if (!newPass || !confirmPass) return dnAlert.error("Please fill all fields");
        if (newPass !== confirmPass) return dnAlert.error("Passwords do not match");
        // Inside D keeps a stricter minimum than the house login — enforced here AND on the
        // server. Weakening it to match dAdmin would be wrong.
        if (newPass.length < 8) return dnAlert.error("Password too short", "Use at least 8 characters.");

        try {
            // currentPass is sent ONLY on the signed-in change-password path (where the old
            // password was just typed). A forgot-password reset has no old password — that
            // path is authorised entirely by the reset-token cookie /verify-otp set.
            const bodyData = { newPass };
            if (oldPass) bodyData.currentPass = oldPass;

            const res = await apiFetch("/api/login/update-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(bodyData),
                credentials: "include",
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            dnAlert.success("Password updated!");
            setOldPass("");
            setNewPass("");
            setConfirmPass("");
            setMode("login");
        } catch (err) {
            dnAlert.error(err.message);
        }
    };

    const handleVerifyOtp = async () => {
        try {
            const res = await apiFetch("/api/login/verify-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp }),
                credentials: "include",
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            dnAlert.success("OTP Verified");
            setTimer(0);
            setOtpSent(false);
            setOtpResetSource(null);
            setMode("setNewPassword");
        } catch (err) {
            dnAlert.error(err.message);
        }
    };

    // Countdown on the emailed sign-in code, separate from the reset timer below.
    useEffect(() => {
        if (otpLeft <= 0) return;
        const t = setInterval(() => setOtpLeft((n) => n - 1), 1000);
        return () => clearInterval(t);
    }, [otpLeft]);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
            return () => clearInterval(interval);
        }
        setOtpSent(false);
    }, [timer]);

    const handleSendOtp = async () => {
        if (!email) return dnAlert.error("Please enter your email");
        if (!EMAIL_RE.test(email)) {
            return dnAlert.error("Invalid Email", "Please enter a valid email address.");
        }

        setOtpLoading(true);
        try {
            const res = await apiFetch("/api/login/send-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();
            if (!res.ok) {
                if (data.message === "EMAIL_NOT_FOUND") {
                    return dnAlert.warning(
                        "Email Not Registered",
                        "This email is not registered with Dolluz Corp. Please contact admin@dolluzcorp.com."
                    );
                }
                throw new Error(data.message || "Failed to send OTP");
            }

            dnAlert.success("OTP Sent!", `Check ${email}`);
            setOtpSent(true);
            setTimer(300); // 5 min — matches the DB OTP expiry + the email text
        } catch (err) {
            dnAlert.error("Error", err.message);
            setOtpSent(false);
            setTimer(0);
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <div className="lg-page">

            {/* ══ LEFT — rotating banner panel, configured in dAdmin ══ */}
            <aside
                className={`lg-panel${bannerImg ? " lg-panel--img" : ""}`}
                style={bannerImg
                    ? { backgroundImage: `url("${bannerImg}")` }
                    : { background: `linear-gradient(140deg, ${active.gradient_from} 0%, ${active.gradient_to} 100%)` }}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                {!bannerImg && (
                    <>
                        <svg className="lg-rings" viewBox="0 0 600 600" aria-hidden="true">
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                                <circle key={n} cx="300" cy="300" r={60 + n * 48}
                                    fill="none" stroke="#fff" strokeWidth="1.4" />
                            ))}
                        </svg>

                        <img className="lg-mark" src={logo_eagle} alt="" aria-hidden="true" />

                        <div className="lg-lock lg-lock--dark lg-lock--on-dark">
                            <img src={DOLLUZ_CORP_reversed} alt="Dolluz Corp" />
                        </div>

                        <div className="lg-panel-body">
                            {active.tag_label && <span className="lg-tag">{active.tag_label}</span>}
                            <h1 className="lg-headline">{active.headline}</h1>
                            {active.subline && <p className="lg-subline">{active.subline}</p>}
                            <div className="lg-rule" />

                            {panelStats.length > 0 && (
                                <div className="lg-stats">
                                    {panelStats.map((st) => (
                                        <div key={st.label}>
                                            <div className="lg-stat-n">{st.value}</div>
                                            <div className="lg-stat-l">{st.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="lg-dots">
                    {panels.map((p, i) => (
                        <button
                            key={p.banner_id ?? i}
                            type="button"
                            className={i === slide ? "lg-dot--on" : ""}
                            onClick={() => setSlide(i)}
                            aria-label={`Show banner ${i + 1}${p.headline ? `: ${p.headline}` : ""}`}
                        />
                    ))}
                </div>
            </aside>

            {/* ══ RIGHT — the form ══ */}
            <main className="lg-form-side">
                <div className="lg-form">

                    <div className="lg-lock lg-lock--sm lg-formlock">
                        <img src={logo_eagle} alt="" />
                        <div>
                            <div className="lg-lock-name">DOLLUZ CORP</div>
                            <div className="lg-lock-sub">INSIDE D PORTAL</div>
                        </div>
                    </div>

                    {mode === "login" && (
                        <>
                            <h2 className="lg-title">Sign in</h2>
                            <p className="lg-lede">Sign in to see the updates, policies and apps meant for you.</p>

                            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                                <div className="lg-field">
                                    <label>Work email</label>
                                    <input
                                        ref={userRef}
                                        type="text"
                                        className="lg-input"
                                        value={username}
                                        autoComplete="username"
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                </div>

                                <PasswordField
                                    label="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    show={showPassword}
                                    setShow={setShowPassword}
                                    inputRef={passRef}
                                    autoComplete="current-password"
                                    capsHint capsOn={capsOn}
                                    onCapsKey={onPasswordKey} onCapsBlur={() => setCapsOn(false)}
                                />

                                <div className="lg-optrow">
                                    {signinCfg.two_factor_enabled === 1 && (
                                        <label className="lg-remember">
                                            <input
                                                type="checkbox"
                                                checked={remember}
                                                onChange={(e) => setRemember(e.target.checked)}
                                            />
                                            Remember for {signinCfg.trust_days} days
                                            <span className="lg-badge">any browser</span>
                                        </label>
                                    )}
                                    <button
                                        type="button"
                                        className="lg-forgot"
                                        onClick={() => {
                                            setOtpResetSource("login");
                                            setMode("otpReset");
                                            setEmail("");
                                            setOtp("");
                                        }}
                                    >
                                        Forgot password
                                    </button>
                                </div>

                                <button type="submit" className="lg-btn" disabled={loading}>
                                    {loading ? "Signing in…" : "Secure Sign-in"}
                                </button>
                            </form>
                        </>
                    )}

                    {mode === "loginOtp" && twoFactor && (
                        <>
                            <h2 className="lg-title">Verify it is you</h2>
                            <p className="lg-lede">
                                Your password was correct. Enter the 6-digit code we sent to{" "}
                                <strong>{twoFactor.email}</strong>.
                            </p>

                            <form onSubmit={(e) => { e.preventDefault(); handleVerifyLoginOtp(); }}>
                                <div className="lg-field">
                                    <label>Sign-in code</label>
                                    <input
                                        className="lg-input lg-input--otp"
                                        value={loginOtp}
                                        inputMode="numeric"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        autoFocus
                                        onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, ""))}
                                    />
                                </div>

                                <div className="lg-optrow">
                                    <span className={otpLeft > 0 ? "lg-timer-inline" : "lg-timer-inline lg-expired"}>
                                        {otpLeft > 0
                                            ? `Expires in ${Math.floor(otpLeft / 60)}:${String(otpLeft % 60).padStart(2, "0")}`
                                            : "Code expired"}
                                    </span>
                                    <button
                                        type="button"
                                        className="lg-forgot"
                                        onClick={handleResendLoginOtp}
                                        disabled={resending}
                                    >
                                        {resending ? "Sending…" : "Resend code"}
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="lg-btn"
                                    disabled={verifying || otpLeft <= 0 || loginOtp.length < 6}
                                >
                                    {verifying ? "Verifying…" : "Verify and sign in"}
                                </button>
                            </form>

                            <button type="button" className="lg-link lg-link--muted" onClick={cancelTwoFactor}>
                                <FaArrowLeft /> Use a different account
                            </button>
                        </>
                    )}

                    {mode === "changePassword" && (
                        <>
                            <h2 className="lg-title">Change password</h2>
                            <p className="lg-lede">Enter your current password to continue.</p>

                            <form onSubmit={(e) => { e.preventDefault(); handleVerifyOldPassword(); }}>
                                <PasswordField
                                    label="Old password"
                                    value={oldPass}
                                    onChange={(e) => setOldPass(e.target.value)}
                                    show={showOldPass}
                                    setShow={setShowOldPass}
                                    autoComplete="current-password"
                                />
                                <button type="submit" className="lg-btn">Continue</button>
                            </form>

                            <button
                                type="button"
                                className="lg-link"
                                onClick={() => { setOtpResetSource("changePassword"); setMode("otpReset"); }}
                            >
                                Change password via OTP
                            </button>
                            <button type="button" className="lg-link lg-link--muted" onClick={backToLogin}>
                                <FaArrowLeft /> Back to sign in
                            </button>
                        </>
                    )}

                    {mode === "otpReset" && (
                        <>
                            <h2 className="lg-title">Reset via OTP</h2>

                            {!otpSent ? (
                                <form onSubmit={(e) => { e.preventDefault(); if (!otpLoading) handleSendOtp(); }}>
                                    <p className="lg-lede">Enter your registered email and we'll send you a code.</p>

                                    <div className="lg-field">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            className="lg-input"
                                            value={email}
                                            autoComplete="username"
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>

                                    <button type="submit" className="lg-btn" disabled={otpLoading}>
                                        {otpLoading ? "Sending OTP…" : "Send OTP"}
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}>
                                    <p className="lg-lede">
                                        OTP sent to <strong>{email}</strong>
                                    </p>

                                    <div className="lg-field">
                                        <label>Enter OTP</label>
                                        <input
                                            type="text"
                                            className="lg-input lg-input--otp"
                                            value={otp}
                                            inputMode="numeric"
                                            maxLength={6}
                                            autoComplete="one-time-code"
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                        />
                                    </div>

                                    <button type="submit" className="lg-btn">Verify OTP</button>

                                    <p className="lg-timer">
                                        OTP expires in{" "}
                                        <strong>{Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}</strong>
                                    </p>
                                </form>
                            )}

                            {otpResetSource === "login" ? (
                                <button type="button" className="lg-link lg-link--muted" onClick={backToLogin}>
                                    <FaArrowLeft /> Back to sign in
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="lg-link lg-link--muted"
                                    onClick={() => setMode("changePassword")}
                                >
                                    <FaArrowLeft /> Back to change password
                                </button>
                            )}
                        </>
                    )}

                    {mode === "setNewPassword" && (
                        <>
                            <h2 className="lg-title">Set a new password</h2>
                            <p className="lg-lede">Choose a password you haven't used before (at least 8 characters).</p>

                            <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}>
                                <PasswordField
                                    label="New password"
                                    value={newPass}
                                    onChange={(e) => setNewPass(e.target.value)}
                                    show={showNewPass}
                                    setShow={setShowNewPass}
                                    autoComplete="new-password"
                                    capsHint capsOn={capsOn}
                                    onCapsKey={onPasswordKey} onCapsBlur={() => setCapsOn(false)}
                                />
                                <PasswordField
                                    label="Confirm new password"
                                    value={confirmPass}
                                    onChange={(e) => setConfirmPass(e.target.value)}
                                    show={showConfirmPass}
                                    setShow={setShowConfirmPass}
                                    autoComplete="new-password"
                                />
                                <button type="submit" className="lg-btn">Update password</button>
                            </form>

                            <button type="button" className="lg-link lg-link--muted" onClick={backToLogin}>
                                <FaArrowLeft /> Back to sign in
                            </button>
                        </>
                    )}

                    <div className="lg-foot">
                        Trouble signing in? Contact{" "}
                        <a href="mailto:admin@dolluzcorp.com">admin@dolluzcorp.com</a>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Login;
